/**
 * 帧候选生成弹窗（image_sequence 模式专用，与 KeyframeGenerateConfirmDialog 同款 UI）
 *
 * 功能：
 * - 写帧 prompt（支持 @ 引用角色 / 物品 / 场景，与单图模式一致）
 * - 实时预估积分
 * - 实时校验参考图数量是否超过模型上限（提交按钮 disable + 红色提示）
 * - 模式切换：注入了 @ 资产时自动走 img2img；否则 text2img
 *
 * 用 React Portal 渲染到 document.body，避免被 inspector 的 backdrop-blur 限制。
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Plus,
  X,
  Coins,
  Sparkles,
  Cpu,
  AlertTriangle,
} from 'lucide-react';
import toast from '../../../../components/Toast';
import { directorApi, DirectorApiError } from '../../services/directorApi';
import { api as imageApi, type ModelConfig } from '../../../../services/api';
import { MentionTextarea, extractMentions, type MentionCandidate, type MentionTextareaHandle } from './MentionTextarea';
import { PromptTemplateBar } from '../shared/PromptTemplateBar';
import {
  DIRECTOR_IMAGE_SIZE_OPTIONS,
  getDefaultDirectorImageSize,
  type DirectorAspectRatio,
  type DirectorImageSize,
} from '../../utils/imageSize';
import type {
  DirectorCharacter,
  DirectorProp,
  DirectorScene,
} from '../../services/directorApi';

const SECTION_LABEL =
  'flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500';
const FIELD =
  'w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:ring-2 focus:ring-amber-100';

const QUALITY_LABEL: Record<string, string> = {
  low: '标准',
  medium: '高清',
  high: '超清',
};

interface Props {
  open: boolean;
  shotId: string;
  projectAspectRatio: DirectorAspectRatio;
  /** 当前分镜的 prompt，用于生成帧时填入 */
  shotPrompt: string;
  currentCredits: number;
  /** 项目可引用的资产列表（用于 @ 唤起候选浮层） */
  projectCharacters: DirectorCharacter[];
  projectProps: DirectorProp[];
  projectScenes: DirectorScene[];
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (input: {
    prompt: string;
    injectedCharacterIds: string[];
    injectedPropIds: string[];
    injectedSceneId: string | null;
    modelId: string;
    size: string;
    quality: 'low' | 'medium' | 'high';
  }) => void;
}

export function FrameGenerateConfirmDialog({
  open,
  shotId,
  projectAspectRatio,
  shotPrompt,
  currentCredits,
  projectCharacters,
  projectProps,
  projectScenes,
  submitting,
  onCancel,
  onConfirm,
}: Props) {
  const [prompt, setPrompt] = useState(shotPrompt);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [modelId, setModelId] = useState('');
  const [size, setSize] = useState<DirectorImageSize>(() =>
    getDefaultDirectorImageSize(projectAspectRatio)
  );
  const [quality, setQuality] = useState<'low' | 'medium' | 'high'>('low');

  // 命令式 ref 给 PromptTemplateBar 用（插到光标位置）
  const promptRef = useRef<MentionTextareaHandle | null>(null);

  const [estimatedCredits, setEstimatedCredits] = useState<number | null>(null);
  const [previewModelName, setPreviewModelName] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  /** 由 prompt 中 @ 解析出的资产 ID */
  const {
    characterIds: injectedCharacterIds,
    propIds: injectedPropIds,
    sceneId: injectedSceneId,
    sceneIdsExtra,
  } = useMemo(
    () =>
      extractMentions(
        prompt,
        projectCharacters.map((c) => ({ id: c.id, name: c.name })),
        projectProps.map((p) => ({ id: p.id, name: p.name })),
        projectScenes.map((s) => ({ id: s.id, name: s.name }))
      ),
    [prompt, projectCharacters, projectProps, projectScenes]
  );

  const mentionedScene = injectedSceneId
    ? projectScenes.find((s) => s.id === injectedSceneId) ?? null
    : null;

  const mentionCandidates: MentionCandidate[] = useMemo(
    () => [
      ...projectCharacters.map((c) => ({
        id: c.id,
        name: c.name,
        kind: 'character' as const,
      })),
      ...projectProps.map((p) => ({
        id: p.id,
        name: p.name,
        kind: 'prop' as const,
      })),
      ...projectScenes.map((s) => ({
        id: s.id,
        name: s.name,
        kind: 'scene' as const,
      })),
    ],
    [projectCharacters, projectProps, projectScenes]
  );

  const refCount =
    injectedCharacterIds.length +
    injectedPropIds.length +
    (mentionedScene && mentionedScene.settingImageJobId ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    setPrompt(shotPrompt);
    setEstimatedCredits(null);
    setPreviewModelName('');
    setSize(getDefaultDirectorImageSize(projectAspectRatio));
    imageApi
      .getModels()
      .then((d) => {
        setModels(d.data);
        if (!modelId && d.data.length > 0) {
          setModelId(d.data[0].id);
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreviewLoading(true);
    directorApi
      .previewFrame(shotId, {
        modelId: modelId || undefined,
        size,
        quality,
      })
      .then((preview) => {
        if (cancelled) return;
        setEstimatedCredits(preview.estimatedCredits);
        setPreviewModelName(preview.modelName);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof DirectorApiError ? err.message : '预估积分失败');
        setEstimatedCredits(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modelId, size, quality]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const insufficient = estimatedCredits !== null && currentCredits < estimatedCredits;
  const canSubmit =
    !!modelId &&
    !!prompt.trim() &&
    estimatedCredits !== null &&
    !insufficient &&
    !submitting;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />
      <div
        className="relative flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-amber-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-amber-200" />
                生成帧候选
              </div>
              <h2 className="text-lg font-semibold leading-tight">为帧序列生成新候选</h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 p-5">
          {/* Prompt */}
          <div className="space-y-1.5">
            <label className={SECTION_LABEL}>提示词</label>
            <MentionTextarea
              ref={promptRef}
              value={prompt}
              onChange={(v) => setPrompt(v.slice(0, 1000))}
              candidates={mentionCandidates}
              placeholder="描述本帧画面，输入 @ 引用角色 / 物品 / 场景"
              rows={3}
              maxLength={1000}
              className={FIELD}
            />
            <p className="text-[11px] text-slate-400">
              输入 @ 可引用项目内的角色 / 物品 / 场景，对应资产图会作为参考图传给模型
            </p>
            {/* 模板快速插入 */}
            <PromptTemplateBar
              onInsert={(text) => promptRef.current?.insertText(text)}
            />
            {/* 注入徽标区 */}
            {refCount > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {injectedCharacterIds.map((cid, idx) => {
                  const c = projectCharacters.find((x) => x.id === cid);
                  if (!c) return null;
                  return (
                    <span
                      key={`char-${cid}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[11px] font-medium text-sky-700"
                    >
                      <span className="rounded-full bg-sky-500 px-1.5 text-[10px] font-semibold text-white">
                        {idx + 1}
                      </span>
                      {c.name}
                    </span>
                  );
                })}
                {injectedPropIds.map((pid, idx) => {
                  const p = projectProps.find((x) => x.id === pid);
                  if (!p) return null;
                  return (
                    <span
                      key={`prop-${pid}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700"
                    >
                      <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
                        {injectedCharacterIds.length + idx + 1}
                      </span>
                      {p.name}
                    </span>
                  );
                })}
                {mentionedScene && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700"
                  >
                    <span className="rounded-full bg-emerald-500 px-1.5 text-[10px] font-semibold text-white">
                      {injectedCharacterIds.length + injectedPropIds.length + 1}
                    </span>
                    {mentionedScene.name}
                  </span>
                )}
              </div>
            )}
            {sceneIdsExtra.length > 0 && mentionedScene && (
              <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-800">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                <span>
                  每帧只能引用一个场景，已采用 <b className="font-mono">@{mentionedScene.name}</b>
                  ；其余 {sceneIdsExtra.length} 个被忽略。
                </span>
              </div>
            )}
          </div>

          {/* 模型 */}
          <div className="space-y-1.5">
            <label className={SECTION_LABEL}>
              <Cpu className="h-3 w-3" /> 图片模型
            </label>
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className={FIELD}
            >
              <option value="">默认（项目设置）</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* 尺寸 + 画质 */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>尺寸</label>
              <div className="grid grid-cols-3 gap-1">
                {DIRECTOR_IMAGE_SIZE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSize(opt.value as typeof size)}
                    className={`rounded-xl border px-2 py-1.5 text-xs font-medium leading-none transition ${
                      size === opt.value
                        ? 'border-slate-950 bg-slate-950 text-white shadow-sm'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className={SECTION_LABEL}>画质</label>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { value: 'low', label: '标准' },
                  { value: 'medium', label: '高清' },
                  { value: 'high', label: '超清' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setQuality(opt.value as typeof quality)}
                    className={`rounded-2xl border px-2 py-2 text-sm font-medium transition ${
                      quality === opt.value
                        ? 'border-slate-950 bg-slate-950 text-white shadow-md'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 模型预览信息 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className="uppercase tracking-[0.18em] text-slate-400">实际模型</span>
              <span className="truncate text-sm font-semibold text-slate-900">
                {previewModelName || '—'}
              </span>
            </div>
            {refCount > 0 && (
              <div className="mt-2 flex items-center justify-between gap-3 text-[11px]">
                <span className="uppercase tracking-[0.18em] text-slate-400">参考图</span>
                <span className="text-slate-700">
                  注入 {refCount} 张（角色 {injectedCharacterIds.length} + 物品{' '}
                  {injectedPropIds.length}
                  {mentionedScene && mentionedScene.settingImageJobId ? ' + 场景 1' : ''}）
                </span>
              </div>
            )}
          </div>

          {/* 积分 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <Coins className="h-3 w-3" /> 预计消耗
              </div>
              <div className="text-base font-semibold text-amber-700">
                {previewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                ) : estimatedCredits !== null ? (
                  `${estimatedCredits} 积分`
                ) : (
                  '—'
                )}
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">当前余额</span>
              <span className={insufficient ? 'text-rose-500' : 'text-slate-600'}>
                {currentCredits} 积分
              </span>
            </div>
          </div>

          {insufficient && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              积分不足，请先充值或兑换
            </div>
          )}

          <p className="text-[11px] leading-5 text-slate-500">
            候选生成后会出现在"候选区"，你可点 + 加入帧序列。生成失败会自动退还积分。
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm({
                prompt,
                injectedCharacterIds,
                injectedPropIds,
                injectedSceneId,
                modelId,
                size,
                quality,
              })
            }
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {estimatedCredits !== null
              ? `确认生成（${estimatedCredits} 积分 / ${QUALITY_LABEL[quality]}）`
              : '确认生成'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
